import { ReactNode, useMemo } from 'react';
import {
	FieldValues,
	FieldPath,
	useController,
	UseControllerProps,
} from 'react-hook-form';
import {
	FormControl,
	FormHelperText,
	TextField,
	Autocomplete as MuiAutocomplete,
	Checkbox,
} from '@mui/material';
import { Option } from '../../../api/DTO/common.interface';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

interface ExtendedOption extends Option {
	childrenList?: ExtendedOption[];
}

interface AppAutocompleteCheckboxProps {
	passObjectValue?: boolean;
	options: ExtendedOption[];
	infoPopUp?: ReactNode;
	removeErrorPadding?: boolean;
	disabled?: boolean;
	handleChange?: (value: (string | number | ExtendedOption)[]) => void;
	formControlStyle?: React.CSSProperties;
	isFullWidth?: boolean;
	isFormHelperText?: boolean;
	optional?: boolean;
	label?: string;
	size?: 'small' | 'medium';
}

// 工具函数：拍平父子结构
const flattenOptions = (options: ExtendedOption[] = []): ExtendedOption[] => {
	const result: ExtendedOption[] = [];
	for (const option of options) {
		if (option.childrenList && option.childrenList.length > 0) {
			result.push({
				...option,
				_disabledAsParent: true,
			});
			result.push(...flattenOptions(option.childrenList));
		} else {
			result.push(option);
		}
	}
	return result;
};

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

export default function AppAutocompleteCheckbox<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
	passObjectValue,
	label,
	options,
	handleChange,
	formControlStyle,
	isFullWidth = true,
	isFormHelperText = true,
	optional = false,
	...props
}: AppAutocompleteCheckboxProps & UseControllerProps<TFieldValues, TName>) {
	const { field, fieldState } = useController(props);
	const errorMessage = fieldState.error?.message;

	// 拍平选项
	const flattenedOptions = useMemo(() => {
		let base = flattenOptions(options);
		if (optional) {
			base = [{ id: '', name: 'None', _isNoneOption: true }, ...base];
		}
		return base;
	}, [options, optional]);

	const isParentOption = (option: ExtendedOption) =>
		(option as any)._disabledAsParent === true;
	const isNoneOption = (option: ExtendedOption) =>
		(option as any)._isNoneOption === true;

	// 当前选中项（数组）
	const selectedOptions = useMemo(() => {
		const valueArray = Array.isArray(field.value) ? field.value : [];

		if (passObjectValue) {
			// 如果传的是对象数组，直接返回（需确保对象有 id）
			return valueArray.filter(
				(item) => item && !isNoneOption(item as ExtendedOption)
			);
		}

		// 否则根据 id 查找完整对象
		return valueArray
			.map((id) =>
				flattenedOptions.find((opt) => opt.id === id && !isNoneOption(opt))
			)
			.filter(Boolean) as ExtendedOption[];
	}, [field.value, flattenedOptions, passObjectValue]);

	const handleOnChange = (_event: any, newValue: ExtendedOption[]) => {
		// 移除 None 和父级（理论上 Autocomplete 不会选中父级，但保险）
		const filtered = newValue.filter(
			(opt) => !isNoneOption(opt) && !isParentOption(opt)
		);

		const finalValue = passObjectValue
			? filtered
			: filtered.map((opt) => opt.id);

		field.onChange(finalValue);
		handleChange?.(finalValue);
	};

	return (
		<FormControl
			error={!!errorMessage}
			fullWidth={isFullWidth}
			size={props.size}
			style={formControlStyle}
		>
			<MuiAutocomplete
				// renderTags={(value, getTagProps) =>
				// 	value.map((option, index) => <span key={index}>{option.name};</span>)
				// }
				disableCloseOnSelect
				multiple
				disabled={props.disabled}
				options={flattenedOptions}
				getOptionLabel={(option) => {
					if (isNoneOption(option)) return 'None';
					return option.name || '';
				}}
				getOptionDisabled={(option) => {
					return isParentOption(option) || isNoneOption(option);
				}}
				isOptionEqualToValue={(option, value) => option.id === value.id}
				renderOption={(renderProps, option, { selected }) => {
					const isParent = isParentOption(option);
					return (
						<li {...renderProps} key={option.id}>
							<Checkbox
								icon={icon}
								checkedIcon={checkedIcon}
								style={{ marginRight: 8 }}
								checked={selected}
								disabled={isParent}
							/>
							{option.icon && option.icon}&nbsp;{option.name}
						</li>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						error={!!errorMessage}
						helperText={undefined}
						size={props.size}
					/>
				)}
				value={selectedOptions}
				onChange={handleOnChange}
				clearOnEscape
				handleHomeEndKeys
				selectOnFocus
				autoHighlight
				freeSolo={false}
				includeInputInList={false}
			/>
			{isFormHelperText && <FormHelperText>{errorMessage}</FormHelperText>}
		</FormControl>
	);
}
